// Agent Analytics Table Data Utilities
// Defines column structure and data manipulation functions for agent analytics table

export const agentAnalyticsColumnDefinitions = [
  {
    id: 'agentName',
    label: 'Agent Name',
    numeric: false,
    sortable: true,
    align: 'left',
    width: '15%',
    sticky: true
  },
  {
    id: 'allocated',
    label: 'Allocated',
    numeric: true,
    sortable: true,
    align: 'right',
    width: '8%'
  },
  {
    id: 'totalAttempted',
    label: 'Total Attempted',
    numeric: true,
    sortable: true,
    align: 'right',
    width: '10%'
  },
  {
    id: 'attemptPercentage',
    label: 'Attempt %',
    numeric: true,
    sortable: true,
    align: 'right',
    width: '9%'
  },
  {
    id: 'connected',
    label: 'Connected',
    numeric: true,
    sortable: true,
    align: 'right',
    width: '8%'
  },
  {
    id: 'unreached',
    label: 'Unreached',
    numeric: true,
    sortable: true,
    align: 'right',
    width: '8%'
  },
  {
    id: 'notInThePortal',
    label: 'Not In Portal',
    numeric: true,
    sortable: true,
    align: 'right',
    width: '9%'
  },
  {
    id: 'childAbove2Years',
    label: 'Child Above 2 Years',
    numeric: true,
    sortable: true,
    align: 'right',
    width: '10%'
  },
  {
    id: 'active',
    label: 'Active',
    numeric: true,
    sortable: true,
    align: 'right',
    width: '7%'
  },
  {
    id: 'pending',
    label: 'Pending',
    numeric: true,
    sortable: true,
    align: 'right',
    width: '7%'
  },
  {
    id: 'unattempted',
    label: 'Unattempted',
    numeric: true,
    sortable: true,
    align: 'right',
    width: '8%'
  }
];/**
 * Calculate totals from agent data
 * @param {Array} data - Array of agent data
 * @returns {Object} Totals row object
 */
export const calculateAgentAnalyticsTotals = (data) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return {
      agentName: 'TOTALS',
      allocated: 0,
      totalAttempted: 0,
      attemptPercentage: 0,
      connected: 0,
      unreached: 0,
      notInThePortal: 0,
      childAbove2Years: 0,
      active: 0,
      pending: 0,
      unattempted: 0
    };
  }

  const totals = data.reduce((acc, agent) => ({
    allocated: acc.allocated + (agent.allocated || 0),
    totalAttempted: acc.totalAttempted + (agent.totalAttempted || 0),
    connected: acc.connected + (agent.connected || 0),
    unreached: acc.unreached + (agent.unreached || 0),
    notInThePortal: acc.notInThePortal + (agent.notInThePortal || 0),
    childAbove2Years: acc.childAbove2Years + (agent.childAbove2Years || 0),
    active: acc.active + (agent.active || 0),
    pending: acc.pending + (agent.pending || 0),
    unattempted: acc.unattempted + (agent.unattempted || 0)
  }), {
    allocated: 0,
    totalAttempted: 0,
    connected: 0,
    unreached: 0,
    notInThePortal: 0,
    childAbove2Years: 0,
    active: 0,
    pending: 0,
    unattempted: 0
  });

  // Calculate overall attempt percentage
  const overallAttemptPercentage = totals.allocated > 0
    ? Number((totals.totalAttempted / totals.allocated * 100).toFixed(2))
    : 0;
  return {
    agentName: 'TOTALS',
    attemptPercentage: overallAttemptPercentage,
    ...totals
  };
};
/**
 * Sort agent analytics data
 * @param {Array} data - Array of agent data
 * @param {string} orderBy - Column to sort by
 * @param {string} order - Sort order (asc/desc)
 * @returns {Array} Sorted data
 */
export const sortAgentAnalyticsData = (data, orderBy, order) => {
  if (!data || !Array.isArray(data)) return [];

  return [...data].sort((a, b) => {
    let aValue = a[orderBy];
    let bValue = b[orderBy];

    // Handle different data types
    if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }

    // Handle null/undefined values
    if (aValue == null) aValue = '';
    if (bValue == null) bValue = '';

    let result = 0;
    if (aValue < bValue) {
      result = -1;
    } else if (aValue > bValue) {
      result = 1;
    }

    return order === 'desc' ? -result : result;
  });
};

/**
 * Get performance indicator color based on attempt percentage
 * @param {number} percentage - Attempt percentage
 * @returns {string} Color name for MUI
 */
export const getPerformanceColor = (percentage) => {
  if (percentage >= 80) return 'success';
  if (percentage >= 60) return 'info';
  if (percentage >= 40) return 'warning';
  return 'error';
};

/**
 * Format percentage for display
 * @param {number} percentage - Raw percentage value
 * @returns {string} Formatted percentage string
 */
export const formatPercentage = (percentage) => {
  if (percentage == null || isNaN(percentage)) return '0%';
  return `${Number(percentage).toFixed(1)}%`;
};
